import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { Form, Input } from 'antd';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

// Date options for dropdowns
const monthOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const dateOptions = Array.from({ length: 31 }, (_, i) => i + 1);

/**
 * StepPassengerDetails - Step 2: Passenger information
 * Uses placeholders for text inputs, labels above selects
 * Errors shown consolidated above the continue button
 */
const StepPassengerDetails = ({ roomsArray = [], onContinue, form }) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState([]);
  let globalPassengerIndex = -1;

  const handleContinue = async () => {
    if (!form) {
      onContinue();
      return;
    }

    try {
      // Build list of passenger fields to validate
      const values = form.getFieldsValue();
      const passengers = values.passengers || [];
      const passengerFields = [];

      passengers.forEach((_, index) => {
        passengerFields.push(
          ['passengers', index, 'first_name'],
          ['passengers', index, 'last_name'],
          ['passengers', index, 'gender'],
          ['passengers', index, 'birth_year'],
          ['passengers', index, 'birth_month'],
          ['passengers', index, 'birth_day']
        );
      });

      await form.validateFields(passengerFields);
      setErrors([]);
      onContinue();
    } catch (errorInfo) {
      // Collect all error messages
      const errorMessages = errorInfo.errorFields?.map(field => {
        const fieldName = field.name[field.name.length - 1];
        const passengerIdx = field.name[1] + 1;
        let fieldLabel = fieldName;

        switch (fieldName) {
          case 'first_name': fieldLabel = t('reservation.steps.passengerDetails.errors.firstNameLabel'); break;
          case 'last_name': fieldLabel = t('reservation.steps.passengerDetails.errors.lastNameLabel'); break;
          case 'gender': fieldLabel = t('reservation.steps.passengerDetails.errors.genderLabel'); break;
          case 'birth_year': fieldLabel = t('reservation.steps.passengerDetails.errors.birthYearLabel'); break;
          case 'birth_month': fieldLabel = t('reservation.steps.passengerDetails.errors.birthMonthLabel'); break;
          case 'birth_day': fieldLabel = t('reservation.steps.passengerDetails.errors.birthDayLabel'); break;
          default: break;
        }

        return {
          name: field.name.join('.'),
          message: `${t('reservation.steps.passengerDetails.passengerNumber', { number: passengerIdx })}: ${fieldLabel} - ${field.errors[0]}`
        };
      }) || [];
      setErrors(errorMessages);
    }
  };

  // Clear errors when user starts typing
  const handleFieldChange = () => {
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  return (
    <div className="step-passenger-details" role="region" aria-label={t('reservation.steps.passengerDetails.title')}>
      <Form.List name="passengers">
        {(fields, { add, remove }) => {
          return (
            <>
              {roomsArray.map((room, roomIdx) => (
                <div key={`room-${room.index}`}>
                  {/* Room header - only show if multiple rooms */}
                  {roomsArray.length > 1 && (
                    <div className={`wizard-section-title ${roomIdx > 0 ? 'wizard-section-title--spaced' : ''}`}>
                      {t('reservation.steps.passengerDetails.roomNumber', { number: room.index })}
                    </div>
                  )}

                  {/* Passengers in this room */}
                  {room.pax.map((passenger, passengerIdx) => {
                    globalPassengerIndex++;
                    const {
                      key,
                      name,
                      passenger: passengerType,
                      passenger_he,
                      passenger_category,
                      fieldKey,
                      age,
                      ...restField
                    } = passenger;

                    const currentYear = new Date().getUTCFullYear();
                    const yearRange = age < 18 ? 20 : 92;

                    return (
                      <div
                        key={`passenger-${room.index}-${passengerIdx}`}
                        className="wizard-passenger-card"
                      >
                        {/* Passenger header */}
                        <div className="wizard-passenger-header">
                          {t('reservation.steps.passengerDetails.passengerNumber', { number: globalPassengerIndex + 1 })}: {passenger_he}
                        </div>

                        {/* Info bar - highlighted passport warning */}
                        <div
                          className="wizard-info-bar wizard-info-bar--highlight wizard-info-bar--compact"
                          role="alert"
                          aria-label={t('reservation.steps.passengerDetails.passportWarning')}
                        >
                          {t('reservation.steps.passengerDetails.passportWarning')}
                        </div>

                        {/* Hidden fields */}
                        <Form.Item
                          {...restField}
                          name={[name, "room_index"]}
                          fieldKey={[fieldKey, "room_index"]}
                          initialValue={room.index}
                          hidden
                        >
                          <Input />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "passenger_index"]}
                          fieldKey={[fieldKey, "passenger_index"]}
                          initialValue={globalPassengerIndex}
                          hidden
                        >
                          <Input />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "category"]}
                          fieldKey={[fieldKey, "category"]}
                          initialValue={passenger_category}
                          hidden
                        />

                        {/* Name fields - inline */}
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Form.Item
                              {...restField}
                              name={[name, "first_name"]}
                              fieldKey={[fieldKey, "first_name"]}
                              rules={[
                                { required: true, message: t('common.labels.required') },
                                { pattern: "^[\x20-\x7F]+$", message: t('common.labels.englishOnly') },
                              ]}
                            >
                              <Input
                                className="wizard-input"
                                placeholder={t('reservation.steps.passengerDetails.firstName')}
                                size="large"
                                aria-label={t('reservation.steps.passengerDetails.firstName')}
                                aria-required="true"
                                onChange={handleFieldChange}
                              />
                            </Form.Item>
                          </Grid>

                          <Grid item xs={6}>
                            <Form.Item
                              {...restField}
                              name={[name, "last_name"]}
                              fieldKey={[fieldKey, "last_name"]}
                              rules={[
                                { required: true, message: t('common.labels.required') },
                                { pattern: "^[\x20-\x7F]+$", message: t('common.labels.englishOnly') },
                              ]}
                            >
                              <Input
                                className="wizard-input"
                                placeholder={t('reservation.steps.passengerDetails.lastName')}
                                size="large"
                                aria-label={t('reservation.steps.passengerDetails.lastName')}
                                aria-required="true"
                                onChange={handleFieldChange}
                              />
                            </Form.Item>
                          </Grid>
                        </Grid>

                        {/* Gender and Birth Date - inline with labels */}
                        <Grid container spacing={1} alignItems="flex-start">
                          {/* Gender */}
                          <Grid item xs={3}>
                            <label className="wizard-select-label" id={`gender-label-${globalPassengerIndex}`}>{t('common.gender.label')}</label>
                            <Form.Item
                              {...restField}
                              name={[name, "gender"]}
                              fieldKey={[fieldKey, "gender"]}
                              rules={[{ required: true, message: t('common.labels.optional') }]}
                            >
                              <Select
                                variant="outlined"
                                displayEmpty
                                className="wizard-select"
                                aria-labelledby={`gender-label-${globalPassengerIndex}`}
                                aria-required="true"
                                onChange={handleFieldChange}
                              >
                                <MenuItem value="" disabled>{t('common.labels.select')}</MenuItem>
                                <MenuItem value="m">{t('common.gender.male')}</MenuItem>
                                <MenuItem value="f">{t('common.gender.female')}</MenuItem>
                              </Select>
                            </Form.Item>
                          </Grid>

                          {/* Birth Year */}
                          <Grid item xs={3}>
                            <label className="wizard-select-label" id={`birth-year-label-${globalPassengerIndex}`}>{t('reservation.steps.passengerDetails.birthDate.year')}</label>
                            <Form.Item
                              {...restField}
                              name={[name, "birth_year"]}
                              fieldKey={[fieldKey, "birth_year"]}
                              rules={[{ required: true, message: t('common.labels.optional') }]}
                            >
                              <Select
                                variant="outlined"
                                displayEmpty
                                className="wizard-select"
                                aria-labelledby={`birth-year-label-${globalPassengerIndex}`}
                                aria-required="true"
                                onChange={handleFieldChange}
                              >
                                <MenuItem value="" disabled>{t('common.labels.select')}</MenuItem>
                                {Array(yearRange)
                                  .fill('')
                                  .map((_, idx) => currentYear - idx)
                                  .map((year) => (
                                    <MenuItem key={year} value={year}>{year}</MenuItem>
                                  ))}
                              </Select>
                            </Form.Item>
                          </Grid>

                          {/* Birth Month */}
                          <Grid item xs={3}>
                            <label className="wizard-select-label" id={`birth-month-label-${globalPassengerIndex}`}>{t('reservation.steps.passengerDetails.birthDate.month')}</label>
                            <Form.Item
                              {...restField}
                              name={[name, "birth_month"]}
                              fieldKey={[fieldKey, "birth_month"]}
                              rules={[{ required: true, message: t('common.labels.optional') }]}
                            >
                              <Select
                                variant="outlined"
                                displayEmpty
                                className="wizard-select"
                                aria-labelledby={`birth-month-label-${globalPassengerIndex}`}
                                aria-required="true"
                                onChange={handleFieldChange}
                              >
                                <MenuItem value="" disabled>{t('common.labels.select')}</MenuItem>
                                {monthOptions.map((month) => (
                                  <MenuItem key={month} value={month}>{month}</MenuItem>
                                ))}
                              </Select>
                            </Form.Item>
                          </Grid>

                          {/* Birth Day */}
                          <Grid item xs={3}>
                            <label className="wizard-select-label" id={`birth-day-label-${globalPassengerIndex}`}>{t('reservation.steps.passengerDetails.birthDate.day')}</label>
                            <Form.Item
                              {...restField}
                              name={[name, "birth_day"]}
                              fieldKey={[fieldKey, "birth_day"]}
                              rules={[{ required: true, message: t('common.labels.optional') }]}
                            >
                              <Select
                                variant="outlined"
                                displayEmpty
                                className="wizard-select"
                                aria-labelledby={`birth-day-label-${globalPassengerIndex}`}
                                aria-required="true"
                                onChange={handleFieldChange}
                              >
                                <MenuItem value="" disabled>{t('common.labels.select')}</MenuItem>
                                {dateOptions.map((date) => (
                                  <MenuItem key={date} value={date}>{date}</MenuItem>
                                ))}
                              </Select>
                            </Form.Item>
                          </Grid>
                        </Grid>
                      </div>
                    );
                  })}
                </div>
              ))}
            </>
          );
        }}
      </Form.List>

      {/* Error Display - Above Button */}
      {errors.length > 0 && (
        <div className="step-error-container" role="alert" aria-live="polite">
          <div className="step-error-title">
            <ErrorOutlineIcon className="step-error-icon" aria-hidden="true" />
            {t('reservation.steps.bookerDetails.errors.fixErrors')}
          </div>
          <ul className="step-error-list" aria-label={t('reservation.steps.passengerDetails.errorList.ariaLabel')}>
            {errors.map((error, idx) => (
              <li key={idx} className="step-error-item">
                {error.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Continue Button */}
      <button
        type="button"
        className="step-continue-btn"
        onClick={handleContinue}
        aria-label={t('common.buttons.continue')}
      >
        {t('common.buttons.continue')}
      </button>
    </div>
  );
};

export default StepPassengerDetails;
